import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Home, Search, ClipboardList, BookOpen, Building2, Sparkles, Plane, Phone, Target, Calendar, Home as HomeIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { TYPEFORM_QUIZ_URL } from "../lib/externalLinks";
import { BRAND } from "../lib/brandConfig";

const NAV_LINKS = [
  { name: "Home",                 path: "/",                    icon: Home,          internal: true },
  { name: "Apartment Search",     path: "/apartment-search",    icon: Search,        internal: true },
  { name: "Match Quiz",           path: TYPEFORM_QUIZ_URL,      icon: ClipboardList, internal: false },
  { name: "Luxury",               path: "/luxury-apartments",   icon: Sparkles,      internal: true },
  { name: "Homes for Rent",       path: "/homes-for-rent",      icon: HomeIcon,      internal: true },
  { name: "Relocation",           path: "/relocation-services", icon: Plane,         internal: true },
  { name: "Blog",                 path: "/blog",                icon: BookOpen,      internal: true },
  { name: "Contact",              path: "/contact",             icon: Phone,         internal: true },
];

// Grouped mobile drawer (Phase 2 — nav simplification)
const MOBILE_GROUPS = [
  { header: "Find a Home", items: [
    { name: "Find My Apartment", path: "/apartment-search",    icon: Search,        internal: true },
    { name: "Match Quiz",        path: TYPEFORM_QUIZ_URL,      icon: ClipboardList, internal: false },
    { name: "Luxury Rentals",    path: "/luxury-apartments",   icon: Sparkles,      internal: true },
    { name: "Homes for Rent",    path: "/homes-for-rent",      icon: HomeIcon,      internal: true },
  ]},
  { header: "Move to Houston", items: [
    { name: "Relocation",        path: "/relocation-services", icon: Plane,         internal: true },
    { name: "Blog",              path: "/blog",                icon: BookOpen,      internal: true },
  ]},
  { header: "Connect", items: [
    { name: "Contact",                  path: "/contact",         icon: Phone,    internal: true },
    { name: "Book a Free Consultation", path: "/book",           icon: Calendar, internal: false },
  ]},
];

const START_SEARCH_LABEL = "Start My Apartment Search";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = (p) => location.pathname === p;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 border-b border-[#D9D9D9] ${isOpen ? "bg-white" : "bg-white/95 backdrop-blur-md"}`} data-testid="primary-nav">
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Brand */}
          <Link to="/" className="flex items-center space-x-3 shrink-0" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#1F2A44] rounded-lg flex items-center justify-center shadow-sm">
              <Building2 className="w-5 h-5 text-[#C8A96B]" />
            </div>
            <div className="hidden sm:flex flex-col leading-tight whitespace-nowrap">
              <span className="font-bold text-lg text-[#1F2A44] font-['Playfair_Display']">
                {BRAND.name}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-semibold">
                {BRAND.brokerageLine}
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-5">
            {NAV_LINKS.filter((l) => l.path !== "/").map((l) => (
              l.internal ? (
                <Link
                  key={l.path}
                  to={l.path}
                  className={`text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive(l.path) ? "text-[#C8A96B]" : "text-[#1F2A44] hover:text-[#C8A96B]"
                  }`}
                  data-testid={`nav-${l.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.name}
                </Link>
              ) : (
                <a
                  key={l.path}
                  href={l.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium whitespace-nowrap text-[#1F2A44] hover:text-[#C8A96B] transition-colors"
                  data-testid={`nav-${l.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.name}
                </a>
              )
            ))}
          </div>

          {/* CTAs (desktop) */}
          <div className="hidden lg:flex items-center space-x-3">
            <a href={TYPEFORM_QUIZ_URL} target="_blank" rel="noopener noreferrer"
               data-testid="cta-match-quiz"
               className="hidden 2xl:inline-flex items-center px-4 py-2 border-2 border-[#C8A96B] text-[#C8A96B] font-semibold text-sm rounded-lg hover:bg-[#C8A96B] hover:text-white transition-colors whitespace-nowrap">
              <ClipboardList className="w-4 h-4 mr-1.5" /> Take the Quiz
            </a>
            <Link to="/apartment-search"
                  data-testid="cta-start-search"
                  className="inline-flex items-center px-4 py-2 bg-[#1F2A44] hover:bg-[#C8A96B] text-white font-semibold text-sm rounded-lg transition-colors whitespace-nowrap">
              <Target className="w-4 h-4 mr-1.5" /> {START_SEARCH_LABEL}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setIsOpen(!isOpen)}
                  className="lg:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
                  data-testid="mobile-menu-toggle">
            {isOpen ? <X className="w-6 h-6 text-[#1F2A44]" /> : <Menu className="w-6 h-6 text-[#1F2A44]" />}
          </button>
        </div>

        {/* Mobile menu — grouped, fully opaque */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-[#D9D9D9] bg-white -mx-4 px-4 sm:-mx-6 sm:px-6 max-h-[calc(100vh-5rem)] overflow-y-auto"
              data-testid="mobile-nav-menu"
            >
              <div className="py-3 bg-white">
                {MOBILE_GROUPS.map((g) => (
                  <div key={g.header} className="mb-2" data-testid={`mobile-nav-group-${g.header.toLowerCase().replace(/\s+/g, '-')}`}>
                    <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">{g.header}</p>
                    {g.items.map((l) => {
                      const cls = `flex items-center space-x-3 px-4 py-3 rounded-md transition-colors ${
                        isActive(l.path) ? "bg-[#1F2A44] text-white" : "text-[#1F2A44] hover:bg-slate-100"}`;
                      const tid = `mobile-nav-${l.name.toLowerCase().replace(/\s+/g, '-')}`;
                      return l.internal ? (
                        <Link key={l.name} to={l.path} onClick={() => setIsOpen(false)} className={cls} data-testid={tid}>
                          <l.icon className="w-5 h-5" /> <span className="font-medium">{l.name}</span>
                        </Link>
                      ) : (
                        <a key={l.name} href={l.path} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)} className={cls} data-testid={tid}>
                          <l.icon className="w-5 h-5" /> <span className="font-medium">{l.name}</span>
                        </a>
                      );
                    })}
                  </div>
                ))}
                <Link to="/apartment-search" onClick={() => setIsOpen(false)}
                      data-testid="mobile-cta-start-search"
                      className="mt-3 mb-2 w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-[#C8A96B] hover:bg-[#B5915A] text-[#1F2A44] font-bold rounded-xl transition-colors">
                  <Target className="w-4 h-4" /> {START_SEARCH_LABEL}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
